// src/hooks/insights/useLiveActiveUsers.ts
//
// Live active-user list behind the Insights Fleet Status "Active Now" card (its count) and
// the Active Users dialog (its rows). SignalRListener joins every org group and keeps
// `OrgGroupUserStatusStore` current from user-status pings, but it never seeds the store's
// initial snapshot - Map.jsx and GroupManagement.tsx each do that on their own mount. A user
// can land on Insights directly, so this hook seeds it the same way, then re-derives the list
// on every store update.

import { useEffect, useMemo, useState } from 'react';
import {
  setGroupUserStatuses,
  useAllGroupUserStatuses,
} from '@/context_providers/group_user_context/OrgGroupUserStatusStore';
import { getAllGroupUserStatusesFromOrg } from '@/repository/UserStatusDAO';
import {
  listActiveUsers,
  type LiveActiveUser,
} from '@/models/Insights/liveActiveUsers';
import type { InsightsSectionState } from '@/hooks/insights/useInsightsData';

/** Outcome of the most recent finished seed, keyed by the org it was for so a stale result
 * from a previous org is never mistaken for the current one. */
type SeedState = {
  orgCode: string | null;
  error: Error | null;
};

const INITIAL_SEED_STATE: SeedState = { orgCode: null, error: null };

/**
 * @param orgCode The signed-in user's own org - the org SignalR group subscriptions are
 *   scoped to. `null` while the organization is still loading.
 */
export function useLiveActiveUsers(
  orgCode: string | null,
): InsightsSectionState<LiveActiveUser[]> {
  const allGroupUserStatuses = useAllGroupUserStatuses();
  const [seedState, setSeedState] = useState<SeedState>(INITIAL_SEED_STATE);

  useEffect(() => {
    if (!orgCode) return;
    let cancelled = false;

    getAllGroupUserStatusesFromOrg(orgCode).then(
      (groupUserStatusesArray) => {
        if (cancelled) return;
        groupUserStatusesArray.forEach((groupUserStatuses) => {
          setGroupUserStatuses(groupUserStatuses.group_name, groupUserStatuses);
        });
        setSeedState({ orgCode, error: null });
      },
      (error: unknown) => {
        if (cancelled) return;
        console.error(
          'Insights: getAllGroupUserStatusesFromOrg failed (Active Now card)',
          error,
        );
        setSeedState({
          orgCode,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      },
    );

    return () => {
      cancelled = true;
    };
  }, [orgCode]);

  const activeUsers = useMemo(
    () => listActiveUsers(allGroupUserStatuses),
    [allGroupUserStatuses],
  );

  // Statuses already in the store (seeded by another page, or pushed by SignalR) are real
  // data, so they win over a still-pending or failed seed of this page's own.
  const hasStoredStatuses = Object.keys(allGroupUserStatuses).length > 0;
  if (hasStoredStatuses) {
    return { data: activeUsers, loading: false, error: null };
  }
  const isSeededForOrg = orgCode !== null && seedState.orgCode === orgCode;
  if (!isSeededForOrg) {
    return { data: null, loading: true, error: null };
  }
  if (seedState.error) {
    return { data: null, loading: false, error: seedState.error };
  }
  return { data: activeUsers, loading: false, error: null };
}
